import { ref, computed } from 'vue';
import { deriveProvenance, provenanceLabel } from './rationaleDraft';

/**
 * Rationale editor state (PD-1301) — the add / edit / view machine from the
 * Claude Design prototype (lvl/wizard.jsx LvlSubQuestion), shared so both
 * portals' presentational components stay thin and behave identically.
 *
 *   add  — "Add rationale" link (optional)
 *   edit — textarea · [Use Atlas | Atlas is writing… | Try again] · Cancel · Save
 *          + "Drafted by Atlas · edit freely before you save" tag once a draft landed
 *          + overwrite confirm when typed text differs from the draft
 *          + error line with Try again on failure (typed text untouched)
 *   view — heading + pencil · provenance line · text
 *
 * The component owns persistence: `save()` returns { text, provenance } and the
 * component emits it; the parent writes it back through `getSaved()`.
 *
 * @param {Object}   o
 * @param {Function} o.getSaved   () => ({ text, provenance }) — the stored rationale (props)
 * @param {Object}   o.runner     a rationaleDraftRun() instance
 */
export function rationaleEditor({ getSaved, runner }) {
  const self = {};

  const saved = () => {
    const s = (getSaved && getSaved()) || {};
    return { text: String(s.text || ''), provenance: s.provenance || null };
  };

  self.mode = ref(saved().text ? 'view' : 'add');
  self.text = ref(saved().text);
  self.aiSeed = ref(''); // this session's Atlas draft, verbatim — provenance is derived against it
  self.askOver = ref(false);

  let _lastRequest = null;

  self.generating = computed(() => runner.state.value === 'generating');
  self.draftError = computed(() => (runner.state.value === 'failed' ? runner.error.value : null));
  self.hasDraft = computed(() => !!self.aiSeed.value);
  self.canSave = computed(() => !!self.text.value.trim() && !self.generating.value);
  self.isDirty = computed(() => self.text.value.trim() !== saved().text.trim());

  self.atlasLabel = computed(() => {
    if (self.generating.value) return 'Atlas is writing…';
    return self.aiSeed.value ? 'Try again' : 'Use Atlas';
  });

  // Provenance the current text WOULD save with — the prototype's saveR rule.
  self.provenanceForSave = computed(() => {
    const s = saved();
    return deriveProvenance({
      text: self.text.value,
      draftText: self.aiSeed.value,
      savedText: s.text,
      savedProvenance: s.provenance,
    });
  });

  // What the view state shows under the heading; null for user-written text.
  self.savedProvenanceLabel = computed(() => provenanceLabel(saved().provenance));

  function _clearDraftState() {
    self.aiSeed.value = '';
    self.askOver.value = false;
    runner.reset();
  }

  self.startAdd = () => {
    self.text.value = '';
    _clearDraftState();
    self.mode.value = 'edit';
  };

  self.startEdit = () => {
    self.text.value = saved().text;
    _clearDraftState();
    self.mode.value = 'edit';
  };

  self.cancel = () => {
    self.text.value = saved().text;
    _clearDraftState();
    self.mode.value = saved().text ? 'view' : 'add';
  };

  /** Returns { text, provenance } for the parent to persist, or null if nothing to save. */
  self.save = () => {
    const text = self.text.value.trim();
    if (!text) return null;
    const provenance = self.provenanceForSave.value;
    self.askOver.value = false;
    self.mode.value = 'view';
    return { text, provenance };
  };

  /** Re-sync after the parent persists or the stored answer changes underneath. */
  self.syncFromSaved = () => {
    const s = saved();
    self.text.value = s.text;
    _clearDraftState();
    self.mode.value = s.text ? 'view' : 'add';
  };

  async function _runAtlas(request) {
    self.askOver.value = false;
    _lastRequest = request;
    await runner.run(request);
    if (runner.state.value === 'done' && runner.draft.value) {
      self.aiSeed.value = runner.draft.value;
      self.text.value = runner.draft.value;
    }
    // On failure the typed text is untouched — the runner only reports.
  }

  /**
   * "Use Atlas" / "Try again". Typed text that is not this session's draft is
   * never replaced without a confirm (prototype: askOver).
   * @param {Object} request — see rationaleDraftRun.run
   */
  self.useAtlas = (request) => {
    const typed = self.text.value.trim();
    if (typed && typed !== self.aiSeed.value.trim()) {
      _lastRequest = request;
      self.askOver.value = true;
      return Promise.resolve();
    }
    return _runAtlas(request);
  };

  self.confirmReplace = () => (_lastRequest ? _runAtlas(_lastRequest) : Promise.resolve());
  self.keepMine = () => { self.askOver.value = false; };
  self.retry = () => (_lastRequest ? _runAtlas(_lastRequest) : Promise.resolve());

  return self;
}
